import React, { useState, useEffect, useRef, useCallback } from "react";
import { MessageCircle, X, Send, ThumbsUp, ThumbsDown, Sparkles, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { axios, API, logError } from "../lib/api";
import { useAuth } from "../auth/AuthProvider";

const STORAGE_KEY = "tch_chat_conv_id";

/**
 * Floating support chat widget — always available.
 *
 * - Anonymous-friendly. Conversation id persisted in localStorage so the
 *   user can come back later in the same browser.
 * - Multi-turn AI replies via /api/support/chat backed by Claude + KB.
 * - Thumbs up/down on AI replies → CSAT feedback loop.
 * - "Talk to a human" button → /api/support/chat/{id}/escalate creates a ticket.
 */
export const SupportChatWidget = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [convId, setConvId] = useState(() => localStorage.getItem(STORAGE_KEY) || null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [hydrating, setHydrating] = useState(false);
  const scrollRef = useRef(null);

  const persistConvId = useCallback((id) => {
    setConvId(id);
    try { localStorage.setItem(STORAGE_KEY, id); }
    catch (err) { logError("support-chat: localStorage.setItem failed", err); }
  }, []);

  // Rehydrate prior conversation on first open
  useEffect(() => {
    if (!open || !convId || messages.length > 0) return;
    setHydrating(true);
    axios.get(`${API}/support/chat/${convId}`)
      .then((r) => setMessages(r.data.messages || []))
      .catch((err) => {
        // Stale id — clear and start fresh
        logError("support-chat: rehydrate failed (likely stale conv_id)", err);
        try { localStorage.removeItem(STORAGE_KEY); }
        catch (e) { logError("support-chat: localStorage.removeItem failed", e); }
        setConvId(null);
      })
      .finally(() => setHydrating(false));
  }, [open, convId, messages.length]);

  // Scroll to latest message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async (e) => {
    e?.preventDefault?.();
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    setDraft("");
    // Optimistic user message
    const temp = { id: `tmp-${Date.now()}`, role: "user", body: text, created_at: new Date().toISOString() };
    setMessages((m) => [...m, temp]);
    try {
      const r = await axios.post(`${API}/support/chat`, {
        message: text, conversation_id: convId, page_url: window.location.pathname,
      });
      const newConvId = r.data.conversation.id;
      if (!convId) persistConvId(newConvId);
      setMessages((m) => {
        const withoutTemp = m.filter((x) => x.id !== temp.id);
        return [...withoutTemp, r.data.user_message, r.data.assistant_message];
      });
    } catch (err) {
      logError("support-chat-send", err);
      setMessages((m) => m.filter((x) => x.id !== temp.id));
      toast.error(err.response?.data?.detail || "Chat is unavailable — please try again.");
    } finally {
      setSending(false);
    }
  };

  const vote = async (messageId, choice) => {
    setMessages((m) => m.map((x) => x.id === messageId ? { ...x, csat: choice } : x));
    try {
      await axios.post(`${API}/support/chat/${convId}/feedback`, {
        message_id: messageId, vote: choice,
      });
    } catch (err) {
      logError("support-chat-feedback", err);
    }
  };

  const escalateToHuman = async () => {
    if (!convId) return;
    const email = user?.email || window.prompt("Your email so a human can reply:");
    if (!email) return;
    try {
      const r = await axios.post(`${API}/support/chat/${convId}/escalate`, { email });
      toast.success(`Ticket opened — reference ${r.data.short_code || r.data.ticket_id.slice(0, 8)}.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not escalate.");
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="support-chat-fab"
        aria-label="Open support chat"
        className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-[#FF6B6B] text-white border-2 border-gray-900 shadow-[4px_4px_0_0_#111827] hover:translate-y-0.5 hover:shadow-[2px_2px_0_0_#111827] transition flex items-center justify-center"
      >
        <MessageCircle className="w-6 h-6" />
      </button>
    );
  }

  return (
    <div
      data-testid="support-chat-panel"
      className="fixed bottom-6 right-6 z-50 w-[min(380px,calc(100vw-2rem))] max-h-[calc(100vh-3rem)] bg-white border-2 border-gray-900 rounded-2xl shadow-[6px_6px_0_0_#111827] flex flex-col overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between bg-gray-900 text-white px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="w-8 h-8 rounded-full bg-[#FFD93D] text-gray-900 flex items-center justify-center">
            <Sparkles className="w-4 h-4" />
          </span>
          <div>
            <div className="text-sm font-black" style={{ fontFamily: "Outfit" }}>ToyCycleHub AI</div>
            <div className="text-[10px] opacity-70">Replies instantly · Cites our policies</div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          data-testid="support-chat-close-btn"
          aria-label="Close support chat"
          className="text-white/70 hover:text-white"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Message list */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto p-3 space-y-3 bg-[#FDFBF7] min-h-[300px] max-h-[55vh]"
        data-testid="support-chat-messages"
      >
        {hydrating && messages.length === 0 && (
          <div className="text-center text-gray-500 text-sm py-8" data-testid="support-chat-hydrating">Loading…</div>
        )}
        {!hydrating && messages.length === 0 && <WelcomeBubble />}
        {messages.map((m) => (
          <ChatBubble key={m.id} message={m} onVote={vote} />
        ))}
        {sending && (
          <div className="flex justify-start" data-testid="support-chat-typing">
            <div className="bg-white border-2 border-gray-900 rounded-2xl px-3 py-2 text-sm italic text-gray-500">
              ToyCycleHub AI is thinking…
            </div>
          </div>
        )}
      </div>

      {/* Escalate */}
      {convId && messages.some((m) => m.should_escalate) && (
        <button
          type="button"
          onClick={escalateToHuman}
          data-testid="support-chat-escalate-btn"
          className="bg-[#FFD93D] text-gray-900 text-xs font-bold py-2 border-t-2 border-gray-900 hover:bg-[#FFC700] transition inline-flex items-center justify-center gap-2"
        >
          <ArrowUpRight className="w-3 h-3" /> Talk to a human
        </button>
      )}

      {/* Composer */}
      <form
        onSubmit={send}
        className="flex items-end gap-2 p-3 border-t-2 border-gray-900 bg-white"
      >
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(e); }
          }}
          rows={1}
          maxLength={2000}
          placeholder="Ask anything — refunds, listings, swaps…"
          data-testid="support-chat-input"
          className="flex-1 resize-none border-2 border-gray-900 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6B6B]"
        />
        <button
          type="submit"
          disabled={sending || draft.trim().length === 0}
          data-testid="support-chat-send-btn"
          aria-label="Send"
          className="w-10 h-10 rounded-full bg-[#FF6B6B] text-white border-2 border-gray-900 shadow-[2px_2px_0_0_#111827] disabled:opacity-50 flex items-center justify-center"
        >
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
};


const WelcomeBubble = () => (
  <div data-testid="support-chat-welcome" className="text-sm text-gray-700 leading-relaxed">
    <p className="font-bold mb-1">👋 Hi there!</p>
    <p>I'm the ToyCycleHub AI assistant. I can help with refunds, disputes, listings, swaps, donations, account questions and more. What can I help you with today?</p>
  </div>
);


const ChatBubble = ({ message, onVote }) => {
  const isUser = message.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        data-testid={`support-chat-msg-${message.id}`}
        className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
          isUser
            ? "bg-[#4A90E2] text-white border-2 border-gray-900"
            : "bg-white text-gray-900 border-2 border-gray-900"
        }`}
      >
        {message.body}
        {!isUser && !message.id.startsWith("tmp-") && (
          <div className="flex items-center gap-1 mt-2 pt-2 border-t border-gray-200">
            <span className="text-[10px] text-gray-500 mr-auto">Helpful?</span>
            <button
              type="button"
              onClick={() => onVote(message.id, "up")}
              data-testid={`support-chat-thumbs-up-${message.id}`}
              aria-label="Helpful"
              className={`p-1 rounded ${message.csat === "up" ? "bg-emerald-500 text-white" : "text-gray-500 hover:bg-gray-100"}`}
            >
              <ThumbsUp className="w-3 h-3" />
            </button>
            <button
              type="button"
              onClick={() => onVote(message.id, "down")}
              data-testid={`support-chat-thumbs-down-${message.id}`}
              aria-label="Not helpful"
              className={`p-1 rounded ${message.csat === "down" ? "bg-[#FF6B6B] text-white" : "text-gray-500 hover:bg-gray-100"}`}
            >
              <ThumbsDown className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
